import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Copy, ExternalLink, Globe, Link2, Link2Off, Printer } from "lucide-react";
import Layout from "../../../layout/Layout";
import {
  apiHata,
  getKonteynerContext,
  getKonteynerPlanSunum,
  getPublicKonteynerPlan,
  kapatKonteynerPaylasim,
  musteriLinki,
  paylasKonteynerPlan,
} from "../../../../api/planlama/konteynerPlanApi";
import Konteyner3DView from "./Konteyner3DView";
import GoktasLogo from "./GoktasLogo";
import "./konteyner.css";
import "./sunum.css";

/**
 * Kayıtlı yükleme planının müşteriye gösterilen sunumu.
 *  - /planning/konteyner-yukleme/plan/:id  giriş + konteynerPlan_view (kendi planı ya da konteynerPlan_viewAll);
 *    konteynerPlan_save yetkisi olan müşteri linki oluşturup kapatabilir.
 *  - /yukleme-plani/:token  (publicMode) girişsiz müşteri linki; link kapalıysa ya da süresi dolduysa açılmaz.
 * Veri: plan kaydedilirken alınan görüntü; burada yeniden hesap yapılmaz.
 */

const FIRMA = "Göktaş Yassı Hadde Mamulleri Sanayi ve Ticaret A.Ş.";

const METIN = {
  tr: {
    baslik: "Konteyner Yükleme Planı",
    icin: (m) => `${m} için hazırlanmıştır`,
    musteri: "Müşteri",
    siparis: "Sipariş",
    tarih: "Plan tarihi",
    konteyner: "Konteyner",
    toplam: "Toplam ağırlık",
    paket: "Paket",
    sira: "Yükleme sırası",
    siraAcik: "Her konteyner yandan, kapı sağda olacak şekilde çizildi. Turuncu ölçü, yükün en üst noktası ile tavan arasındaki boşluktur. Ayrıntı için bir konteyner seçin.",
    no: (n, t) => `Konteyner ${n} / ${t}`,
    tavan: "Tavana boşluk",
    tavanAcik: "En üstteki paketin üst yüzeyi ile konteyner tavanı arasındaki mesafe.",
    tavanMin: (mm) => `Boşaltmada forklift paketleri üstteki halatlardan kaldırdığı için tavana en az ${mm} mm boşluk bırakıldı.`,
    sinir: "Yük sınırı",
    kapi: "Kapı üst kenarına boşluk",
    yukYuk: "Yük yüksekliği",
    icYuk: "İç yükseklik",
    kapiYuk: "Kapı yüksekliği",
    icEn: "İç genişlik",
    yukBoy: "Yük boyu / iç boy",
    agirlik: "Yük ağırlığı",
    azami: "azami",
    kesit: "Kapıdan görünüş",
    kesitAcik: "Konteyner kapısından bakıldığında paketlerin dizilişi ve ölçüler.",
    icerik: "Bu konteynerde",
    urun: "Ürün",
    parca: "Parça",
    kg: "Ağırlık (kg)",
    dizilim: "Diziliş",
    bosaltma: "Boşaltma, yükleme sırasının tersidir: kapıya en yakın paket ilk çıkar. 3D görünümde Boşalt ile sırayı izleyebilirsiniz.",
    hepsi: "Konteyner özeti",
    siparisOzet: "Sipariş içeriği",
    konteynerler: "Konteynerler",
    kurallar: "Uygulanan yükleme talimatı",
    madde: "Madde",
    olcu: "Tüm ölçüler milimetredir. Bu plan yükleme öncesinde hazırlanmıştır; sahada küçük farklar olabilir.",
    geri: "Planlama ekranı",
    yazdir: "Yazdır",
    dil: "English",
    yukleniyor: "Yükleme planı açılıyor…",
    gecersiz: "Bu yükleme planı linki geçersiz veya süresi dolmuş. Lütfen Göktaş Metal ile iletişime geçin.",
    urunSay: (n) => `${n} ürün`,
    kapiYazi: "kapı",
  },
  en: {
    baslik: "Container Loading Plan",
    icin: (m) => `Prepared for ${m}`,
    musteri: "Customer",
    siparis: "Order",
    tarih: "Plan date",
    konteyner: "Containers",
    toplam: "Total weight",
    paket: "Bundles",
    sira: "Loading sequence",
    siraAcik: "Each container is drawn from the side with the door on the right. The orange dimension is the space between the top of the load and the roof. Select a container for details.",
    no: (n, t) => `Container ${n} of ${t}`,
    tavan: "Roof clearance",
    tavanAcik: "Distance between the top of the highest bundle and the container roof.",
    tavanMin: (mm) => `At least ${mm} mm is kept free below the roof so the forklift can lift the bundles by their top slings.`,
    sinir: "Load limit",
    kapi: "Clearance to door header",
    yukYuk: "Load height",
    icYuk: "Inside height",
    kapiYuk: "Door height",
    icEn: "Inside width",
    yukBoy: "Load length / inside length",
    agirlik: "Cargo weight",
    azami: "max",
    kesit: "View from the door",
    kesitAcik: "How the bundles are stowed, seen from the container door, with dimensions.",
    icerik: "In this container",
    urun: "Product",
    parca: "Pieces",
    kg: "Weight (kg)",
    dizilim: "Stowage",
    bosaltma: "Unloading is the reverse of loading: the bundle closest to the door comes out first. Use Unload in the 3D view to follow the sequence.",
    hepsi: "Container summary",
    siparisOzet: "Order contents",
    konteynerler: "Containers",
    kurallar: "Applied loading instruction",
    madde: "Item",
    olcu: "All dimensions in millimetres. This plan was prepared before loading; minor differences may occur on site.",
    geri: "Planning screen",
    yazdir: "Print",
    dil: "Türkçe",
    yukleniyor: "Opening loading plan…",
    gecersiz: "This loading plan link is invalid or has expired. Please contact Göktaş Metal.",
    urunSay: (n) => `${n} products`,
    kapiYazi: "door",
  },
};

const MOD = {
  tr: { "duz-boyuna": "Düz, boyuna", "petek-boyuna": "Petek, boyuna", "duz-enine": "Düz, enine", "petek-enine": "Petek, enine" },
  en: { "duz-boyuna": "Flat, lengthwise", "petek-boyuna": "Honeycomb, lengthwise", "duz-enine": "Flat, crosswise", "petek-enine": "Honeycomb, crosswise" },
};

const locale = (lang) => (lang === "en" ? "en-GB" : "tr-TR");
const ilkDil = () => {
  try {
    const q = new URLSearchParams(window.location.search).get("lang");
    return q === "en" ? "en" : "tr";
  } catch {
    return "tr";
  }
};

const PublicKabuk = ({ children }) => <div className="kys-public">{children}</div>;

export default function KonteynerSunumPage({ publicMode = false }) {
  const { id, token } = useParams();
  const Kabuk = publicMode ? PublicKabuk : Layout;
  const [paylasim, setPaylasim] = useState(null);
  const [linkYetki, setLinkYetki] = useState(false);
  const [linkAcik, setLinkAcik] = useState(false);
  const [lang, setLang] = useState(ilkDil);
  const [data, setData] = useState(null);
  const [hata, setHata] = useState("");
  const [no, setNo] = useState(1);
  const T = METIN[lang];
  const f = (v, d = 0) => (Number.isFinite(v) ? v.toLocaleString(locale(lang), { minimumFractionDigits: d, maximumFractionDigits: d }) : "");

  useEffect(() => {
    (async () => {
      try {
        const r = publicMode ? await getPublicKonteynerPlan(token) : await getKonteynerPlanSunum(id);
        setData(r.data.data);
        setPaylasim(r.data.data.paylasim || null);
      } catch (e) {
        setHata(publicMode && e?.response?.status === 404 ? "404" : apiHata(e));
      }
    })();
  }, [id, token, publicMode]);

  useEffect(() => {
    if (publicMode) return;
    getKonteynerContext()
      .then((r) => setLinkYetki(!!r.data.data.yetkiler?.planKaydet))
      .catch(() => setLinkYetki(false));
  }, [publicMode]);

  useEffect(() => {
    if (!publicMode) return undefined;
    // müşteri linki arama motorlarına kapalı
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => m.remove();
  }, [publicMode]);

  useEffect(() => {
    if (!data) return undefined;
    const once = document.title;
    document.title = `${T.baslik} – ${data.musteriAd}${data.siparisNo ? ` ${data.siparisNo}` : ""}`;
    return () => {
      document.title = once;
    };
  }, [data, T.baslik]);

  const g = data?.goruntu;
  const satir = useMemo(() => Object.fromEntries((g?.satirlar || []).map((s) => [s.id, s])), [g]);
  const c = g?.containers.find((x) => x.no === no) || g?.containers[0];
  const icerik = useMemo(() => {
    if (!c) return [];
    const by = {};
    for (const p of c.placed) {
      const b = (by[p.rowId] ||= { id: p.rowId, paket: 0, parca: 0, kg: 0, modlar: {} });
      b.paket++;
      b.parca += p.pieces;
      b.kg += p.kg;
      b.modlar[p.mod] = (b.modlar[p.mod] || 0) + 1;
    }
    return Object.values(by);
  }, [c]);

  const colorOf = (rid) => satir[rid]?.renk || "#2f6f98";
  const labelOf = (rid) => satir[rid]?.etiket || rid;

  const arac = (
    <div className={`kys-tools${publicMode ? " end" : ""}`}>
      {!publicMode && (
        <Link to="/planning/konteyner-yukleme" className="kys-tool">
          <ArrowLeft size={15} /> {T.geri}
        </Link>
      )}
      <div>
        {!publicMode && linkYetki && data && (
          <button type="button" className="kys-tool" aria-expanded={linkAcik} onClick={() => setLinkAcik(!linkAcik)}>
            <Link2 size={15} /> Müşteri linki
            {paylasim?.aktif && new Date(paylasim.sonGecerlilik) > new Date() ? <span className="kys-dot" aria-label="açık" /> : null}
          </button>
        )}
        <button type="button" className="kys-tool" onClick={() => window.print()}>
          <Printer size={15} /> {T.yazdir}
        </button>
        <button type="button" className="kys-tool" onClick={() => setLang(lang === "tr" ? "en" : "tr")} lang={lang === "tr" ? "en" : "tr"}>
          <Globe size={15} /> {T.dil}
        </button>
      </div>
      {!publicMode && linkAcik && data && <PaylasimPaneli planId={id} paylasim={paylasim} onChange={setPaylasim} />}
    </div>
  );

  if (hata || !data) {
    return (
      <Kabuk>
        <div className="kys" lang={lang}>
          {arac}
          <article className="kys-doc kys-msg">
            <GoktasLogo className="kys-logo" />
            <h1>{T.baslik}</h1>
            <p>{hata === "404" ? T.gecersiz : hata || T.yukleniyor}</p>
          </article>
        </div>
      </Kabuk>
    );
  }

  const toplamPaket = g.containers.reduce((t, k) => t + k.placed.length, 0);
  const fleet = Object.entries(g.meta.fleet).map(([n, k]) => `${k} × ${n}`).join(" + ");
  const maxL = Math.max(...g.containers.map((k) => k.cont.L));

  return (
    <Kabuk>
      <div className="kys" lang={lang}>
        {arac}
        <article className="kys-doc">
          {/* başlık */}
          <header className="kys-head">
            <GoktasLogo className="kys-logo" />
            <div className="kys-firma">{FIRMA}</div>
            <h1>{T.baslik}</h1>
            <p className="kys-for">{T.icin(data.musteriAd)}</p>
          </header>

          <dl className="kys-info">
            <div>
              <dt>{T.musteri}</dt>
              <dd>{data.musteriAd}</dd>
            </div>
            <div>
              <dt>{T.siparis}</dt>
              <dd>{data.siparisNo || data.baslik || "—"}</dd>
            </div>
            <div>
              <dt>{T.tarih}</dt>
              <dd>{new Date(data.createdAt).toLocaleDateString(locale(lang))}</dd>
            </div>
            <div>
              <dt>{T.konteyner}</dt>
              <dd>{fleet}</dd>
            </div>
            <div>
              <dt>{T.toplam}</dt>
              <dd>{f(g.meta.placedKg)} kg</dd>
            </div>
            <div>
              <dt>{T.paket}</dt>
              <dd>
                {f(toplamPaket)} <small>({T.urunSay(g.satirlar.length)})</small>
              </dd>
            </div>
          </dl>

          {/* yükleme sırası: konteynerlerin yandan çizimi */}
          <section className="kys-sec">
            <h2>{T.sira}</h2>
            <p className="kys-lead">{T.siraAcik}</p>
            <ol className="kys-seq">
              {g.containers.map((k) => (
                <li key={k.no}>
                  <button type="button" aria-pressed={k.no === c.no} onClick={() => setNo(k.no)}>
                    <Yandan c={k} maxL={maxL} colorOf={colorOf} f={f} kapi={T.kapiYazi} />
                    <span className="kys-seq-cap">
                      <b>{k.no}</b>
                      <span>
                        {k.cont.name}
                        <small>
                          {f(k.stats.kg / 1000, 1)} t, {f(k.placed.length)} {T.paket.toLowerCase()}
                        </small>
                      </span>
                      <span className="kys-gap">
                        {f(k.stats.tavanBosluk)}
                        <small>mm</small>
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          </section>

          {/* seçili konteyner */}
          <section className="kys-sec kys-cur">
            <div className="kys-cur-head">
              <h2>
                {T.no(c.no, g.containers.length)} <span>{c.cont.name}</span>
              </h2>
              <div className="kys-pager">
                <button type="button" disabled={c.no === g.containers[0].no} onClick={() => setNo(g.containers[g.containers.indexOf(c) - 1].no)} aria-label="‹">
                  ‹
                </button>
                <button type="button" disabled={c.no === g.containers[g.containers.length - 1].no} onClick={() => setNo(g.containers[g.containers.indexOf(c) + 1].no)} aria-label="›">
                  ›
                </button>
              </div>
            </div>

            <div className="kyp-page kys-viewer">
              <Konteyner3DView key={lang} lang={lang} result={g} no={c.no} colorOf={colorOf} labelOf={labelOf} title={`${c.cont.name}`} />
            </div>
            <p className="kys-note">{T.bosaltma}</p>

            <div className="kys-cur-grid">
              <figure className="kys-section-fig">
                <figcaption>
                  <h3>{T.kesit}</h3>
                  <p>{T.kesitAcik}</p>
                </figcaption>
                <Kapidan c={c} colorOf={colorOf} f={f} T={T} />
              </figure>

              <div className="kys-facts">
                <div className="kys-clear">
                  <span>{T.tavan}</span>
                  <strong>
                    {f(c.stats.tavanBosluk)} <small>mm</small>
                  </strong>
                  <p>{T.tavanAcik}</p>
                  {!!c.U.minTavan && <p className="kys-min">{T.tavanMin(f(c.U.minTavan))}</p>}
                </div>
                <dl className="kys-kv">
                  <dt>{T.kapi}</dt>
                  <dd>{f(c.stats.kapiBosluk)} mm</dd>
                  <dt>{T.yukYuk}</dt>
                  <dd>{f(c.stats.loadHeight)} mm</dd>
                  <dt>{T.icYuk}</dt>
                  <dd>{f(c.cont.H)} mm</dd>
                  <dt>{T.kapiYuk}</dt>
                  <dd>{f(c.cont.doorH)} mm</dd>
                  <dt>{T.yukBoy}</dt>
                  <dd>
                    {f(c.stats.loadLen)} / {f(c.cont.L)} mm
                  </dd>
                  <dt>{T.agirlik}</dt>
                  <dd>
                    {f(c.stats.kg)} kg <small>
                      ({T.azami} {f(c.U.maxKg)})
                    </small>
                  </dd>
                </dl>

                <h3>{T.icerik}</h3>
                <table className="kys-table">
                  <thead>
                    <tr>
                      <th>{T.urun}</th>
                      <th className="num">{T.paket}</th>
                      <th className="num">{T.parca}</th>
                      <th className="num">{T.kg}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {icerik.map((b) => (
                      <tr key={b.id}>
                        <td>
                          <span className="kys-sw" style={{ background: colorOf(b.id) }} />
                          {labelOf(b.id)}
                          <small>{Object.keys(b.modlar).map((m) => MOD[lang][m] || m).join(" / ")}</small>
                        </td>
                        <td className="num">{f(b.paket)}</td>
                        <td className="num">{f(b.parca)}</td>
                        <td className="num">{f(b.kg)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* özetler */}
          <section className="kys-sec">
            <h2>{T.hepsi}</h2>
            <div className="kys-scroll">
              <table className="kys-table">
                <thead>
                  <tr>
                    <th>{T.konteyner}</th>
                    <th className="num">{T.paket}</th>
                    <th className="num">{T.kg}</th>
                    <th className="num">{T.yukYuk}</th>
                    <th className="num">{T.tavan}</th>
                  </tr>
                </thead>
                <tbody>
                  {g.containers.map((k) => (
                    <tr key={k.no} className={k.no === c.no ? "is-cur" : ""}>
                      <td>
                        <button type="button" className="kys-rowbtn" onClick={() => setNo(k.no)}>
                          {k.no}. {k.cont.name}
                        </button>
                      </td>
                      <td className="num">{f(k.placed.length)}</td>
                      <td className="num">{f(k.stats.kg)}</td>
                      <td className="num">{f(k.stats.loadHeight)} mm</td>
                      <td className="num kys-or">{f(k.stats.tavanBosluk)} mm</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="kys-sec">
            <h2>{T.siparisOzet}</h2>
            <div className="kys-scroll">
              <table className="kys-table">
                <thead>
                  <tr>
                    <th>{T.urun}</th>
                    <th>{T.dizilim}</th>
                    <th className="num">{T.paket}</th>
                    <th className="num">{T.kg}</th>
                    <th>{T.konteynerler}</th>
                  </tr>
                </thead>
                <tbody>
                  {g.satirlar.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <span className="kys-sw" style={{ background: s.renk }} />
                        {s.etiket}
                      </td>
                      <td>{Object.keys(s.modlar || {}).map((m) => MOD[lang][m] || m).join(" / ")}</td>
                      <td className="num">{f(s.paket)}</td>
                      <td className="num">{f(s.kg)}</td>
                      <td>
                        {g.containers
                          .filter((k) => k.placed.some((p) => p.rowId === s.id))
                          .map((k) => k.no)
                          .join(", ")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!!g.kurallar?.length && (
              <div className="kys-rules">
                <h3>{T.kurallar}</h3>
                <ul>
                  {g.kurallar.map((k, i) => (
                    <li key={i}>
                      {k.ad}
                      {k.madde ? <span>{T.madde} {String(k.madde).replace(/^madde\s*/i, "")}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          <footer className="kys-foot">
            <GoktasLogo className="kys-foot-logo" />
            <div>
              <b>{FIRMA}</b>
              <span>{T.olcu}</span>
            </div>
          </footer>
        </article>
      </div>
    </Kabuk>
  );
}

/** Müşteri linki: oluştur, kopyala, süreyi yenile, kapat. Yalnız iç sunum sayfasında, konteynerPlan_save yetkisiyle. */
function PaylasimPaneli({ planId, paylasim, onChange }) {
  const [gun, setGun] = useState(90);
  const [busy, setBusy] = useState(false);
  const [mesaj, setMesaj] = useState("");
  const gecerli = paylasim?.aktif && paylasim?.token && new Date(paylasim.sonGecerlilik) > new Date();
  const url = paylasim?.token ? musteriLinki(paylasim.token) : "";

  const olustur = async () => {
    setBusy(true);
    setMesaj("");
    try {
      const r = await paylasKonteynerPlan(planId, gun);
      onChange({ ...(paylasim || {}), ...r.data.data, aktif: true });
      setMesaj(gecerli ? "Süre yenilendi." : "Link hazır. Kopyalayıp müşteriye gönderebilirsiniz.");
    } catch (e) {
      setMesaj(apiHata(e));
    } finally {
      setBusy(false);
    }
  };
  const kapat = async () => {
    if (!window.confirm("Müşteri linki kapatılsın mı? Link artık açılmaz.")) return;
    setBusy(true);
    try {
      await kapatKonteynerPaylasim(planId);
      onChange({ ...paylasim, aktif: false });
      setMesaj("Link kapatıldı.");
    } catch (e) {
      setMesaj(apiHata(e));
    } finally {
      setBusy(false);
    }
  };
  const kopyala = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setMesaj("Link kopyalandı.");
    } catch {
      window.prompt("Linki kopyalayın:", url);
    }
  };

  return (
    <section className="kys-share" aria-label="Müşteri linki">
      {gecerli ? (
        <>
          <p>
            Giriş gerektirmeyen link açık: <b>{new Date(paylasim.sonGecerlilik).toLocaleDateString("tr-TR")}</b> tarihine kadar geçerli,{" "}
            {paylasim.goruntulenme || 0} kez açıldı. Linki yalnız müşterinizle paylaşın.
          </p>
          <div className="kys-share-row">
            <input readOnly value={url} onFocus={(e) => e.target.select()} aria-label="Müşteri linki" />
            <button type="button" className="kys-tool primary" onClick={kopyala}>
              <Copy size={15} /> Kopyala
            </button>
            <a className="kys-tool" href={url} target="_blank" rel="noreferrer">
              <ExternalLink size={15} /> Aç
            </a>
          </div>
          <div className="kys-share-row">
            <select value={gun} onChange={(e) => setGun(+e.target.value)} aria-label="Yeni geçerlilik süresi">
              <option value={30}>30 gün</option>
              <option value={90}>90 gün</option>
              <option value={365}>1 yıl</option>
            </select>
            <button type="button" className="kys-tool" disabled={busy} onClick={olustur}>
              Süreyi yenile
            </button>
            <button type="button" className="kys-tool danger" disabled={busy} onClick={kapat}>
              <Link2Off size={15} /> Linki kapat
            </button>
          </div>
        </>
      ) : (
        <>
          <p>
            Müşteri bu sunumu giriş yapmadan açabilsin. Link tahmin edilemez ve seçtiğiniz süre sonunda kendiliğinden kapanır.
            {paylasim?.token ? " Daha önce kapatılan link aynı adresle yeniden açılır." : ""}
          </p>
          <div className="kys-share-row">
            <select value={gun} onChange={(e) => setGun(+e.target.value)} aria-label="Geçerlilik süresi">
              <option value={30}>30 gün geçerli</option>
              <option value={90}>90 gün geçerli</option>
              <option value={365}>1 yıl geçerli</option>
            </select>
            <button type="button" className="kys-tool primary" disabled={busy} onClick={olustur}>
              <Link2 size={15} /> Link oluştur
            </button>
          </div>
        </>
      )}
      {mesaj && <p className="kys-share-msg" role="status">{mesaj}</p>}
    </section>
  );
}

/** Konteynerin yandan çizimi (x: boy, kapı sağda). Turuncu ölçü: yükün tepesi ile tavan arası. */
function Yandan({ c, maxL, colorOf, f, kapi }) {
  const Wpx = 248 * (c.cont.L / maxL);
  const s = Wpx / c.cont.L;
  const Hpx = c.cont.H * s;
  const top = 26, left = 4;
  const enUst = c.placed.reduce((a, p) => (p.z + p.h > a.z + a.h ? p : a), c.placed[0]);
  const yTop = top + (c.cont.H - (enUst.z + enUst.h)) * s;
  const gx = left + (enUst.x + enUst.len / 2) * s;
  // aynı x-z kesitinde üst üste binen paketleri bir kez çiz
  const kutular = [];
  const seen = new Set();
  for (const p of c.placed) {
    const k = `${p.x}|${p.z}|${p.len}|${p.h}|${p.rowId}`;
    if (seen.has(k)) continue;
    seen.add(k);
    kutular.push(p);
  }
  return (
    <svg className="kys-elev" viewBox={`0 0 ${Wpx + left * 2 + 18} ${top + Hpx + 8}`} aria-hidden="true">
      <rect x={left} y={top} width={Wpx} height={Hpx} className="kys-elev-box" />
      {c.U.taban > 0 && <rect x={left} y={top + Hpx - c.U.taban * s} width={c.stats.loadLen * s} height={c.U.taban * s} className="kys-wood" />}
      {kutular.map((p) => (
        <rect key={p.id} x={left + p.x * s + 0.7} y={top + (c.cont.H - p.z - p.h) * s} width={Math.max(0.6, p.len * s - 1.4)} height={Math.max(0.6, p.h * s - 0.3)} fill={colorOf(p.rowId)} opacity=".88" />
      ))}
      {/* kapı */}
      <line x1={left + Wpx} x2={left + Wpx + 9} y1={top} y2={top - 4} className="kys-door" />
      <line x1={left + Wpx} x2={left + Wpx + 9} y1={top + Hpx} y2={top + Hpx + 4} className="kys-door" />
      <text x={left + Wpx + 3} y={top + Hpx / 2 + 3} className="kys-door-t" transform={`rotate(-90 ${left + Wpx + 6} ${top + Hpx / 2})`}>
        {kapi}
      </text>
      {!!c.U.minTavan && <line x1={left} x2={left + Wpx} y1={top + c.U.minTavan * s} y2={top + c.U.minTavan * s} className="kys-limit" />}
      {/* tavan boşluğu ölçüsü */}
      <line x1={gx} x2={gx} y1={top} y2={yTop} className="kys-dim" />
      <line x1={gx - 4} x2={gx + 4} y1={yTop} y2={yTop} className="kys-dim" />
      <line x1={gx} x2={gx} y1={top - 3} y2={top - 12} className="kys-dim thin" />
      <text x={gx} y={top - 15} textAnchor="middle" className="kys-dim-t">
        {f(c.stats.tavanBosluk)}
      </text>
    </svg>
  );
}

const hexPts = (x, y, w, h, petek) =>
  (petek
    ? [[x + w / 2, y], [x + w, y + h / 4], [x + w, y + (3 * h) / 4], [x + w / 2, y + h], [x, y + (3 * h) / 4], [x, y + h / 4]]
    : [[x, y + h / 2], [x + w / 4, y], [x + (3 * w) / 4, y], [x + w, y + h / 2], [x + (3 * w) / 4, y + h], [x + w / 4, y + h]]
  )
    .map((q) => q.map((v) => v.toFixed(1)).join(","))
    .join(" ");

/** Kapıdan görünüş (y: en, z: yükseklik) ve ölçü çizgileri. */
function Kapidan({ c, colorOf, f, T }) {
  const { W, H } = c.cont;
  const doorH = c.cont.doorH || H;
  const s = 300 / H;
  const Wp = W * s, Hp = H * s;
  const L = 74, R = 128, TOP = 16, B = 40; // kenar boşlukları (ölçüler için)
  const x0 = L, y0 = TOP;
  const Y = (z) => y0 + (H - z) * s;
  const X = (y) => x0 + y * s;
  const yuk = c.stats.loadHeight;
  const seen = new Set();
  const kes = [];
  // kapıya en yakın paketler önde görünür: önce arkadakileri çiz
  for (const p of [...c.placed].sort((a, b) => a.x + a.len - (b.x + b.len))) {
    const k = `${Math.round(p.y)}|${Math.round(p.z)}|${p.enine ? 1 : 0}`;
    if (seen.has(k)) continue;
    seen.add(k);
    kes.push(p);
  }
  const ox = x0 + Wp; // sağ ölçü hattı başlangıcı
  return (
    <svg className="kys-door-fig" viewBox={`0 0 ${L + Wp + R} ${TOP + Hp + B}`} role="img" aria-label={`${T.tavan} ${f(c.stats.tavanBosluk)} mm`}>
      <rect x={x0} y={y0} width={Wp} height={Hp} className="kys-sec-box" />
      {c.U.taban > 0 && <rect x={x0 + 2} y={Y(c.U.taban)} width={Wp - 4} height={c.U.taban * s} className="kys-wood" />}
      {kes.map((p) =>
        p.enine ? (
          <rect key={p.id} x={X(p.y)} y={Y(p.z + p.h)} width={p.w * s} height={p.h * s} fill={colorOf(p.rowId)} className="kys-pk" />
        ) : (
          <polygon key={p.id} points={hexPts(X(p.y), Y(p.z + p.h), p.w * s, p.h * s, p.petek)} fill={colorOf(p.rowId)} className="kys-pk" />
        )
      )}
      {/* istenen en az tavan boşluğu: yük sınırı */}
      {!!c.U.minTavan && (
        <>
          <line x1={x0} x2={ox} y1={Y(H - c.U.minTavan)} y2={Y(H - c.U.minTavan)} className="kys-limit" />
          <text x={ox - 4} y={Y(H - c.U.minTavan) - 4} textAnchor="end" className="kys-small">
            {T.sinir} {f(H - c.U.minTavan)}
          </text>
        </>
      )}
      {/* kapı yüksekliği */}
      <line x1={x0} x2={ox} y1={Y(doorH)} y2={Y(doorH)} className="kys-doorline" />
      <text x={x0 + 4} y={Y(doorH) - 4} className="kys-small">
        {T.kapiYuk} {f(doorH)}
      </text>

      {/* sol: iç yükseklik */}
      <g className="kys-dimline">
        <line x1={x0 - 30} x2={x0 - 30} y1={Y(H)} y2={Y(0)} />
        <line x1={x0 - 36} x2={x0 - 4} y1={Y(H)} y2={Y(H)} className="ext" />
        <line x1={x0 - 36} x2={x0 - 4} y1={Y(0)} y2={Y(0)} className="ext" />
        <text x={x0 - 36} y={Y(H / 2)} textAnchor="middle" transform={`rotate(-90 ${x0 - 36} ${Y(H / 2)})`}>
          {f(H)}
        </text>
      </g>

      {/* sağ: yük yüksekliği */}
      <g className="kys-dimline">
        <line x1={ox + 20} x2={ox + 20} y1={Y(yuk)} y2={Y(0)} />
        <line x1={ox + 4} x2={ox + 26} y1={Y(yuk)} y2={Y(yuk)} className="ext" />
        <line x1={ox + 4} x2={ox + 26} y1={Y(0)} y2={Y(0)} className="ext" />
        <text x={ox + 28} y={Y(yuk / 2) + 4}>
          {f(yuk)}
        </text>
      </g>

      {/* sağ: tavan boşluğu (vurgulu) */}
      <g className="kys-dimline gap">
        <line x1={x0} x2={ox + 46} y1={Y(yuk)} y2={Y(yuk)} className="ext" />
        <line x1={ox + 4} x2={ox + 46} y1={Y(H)} y2={Y(H)} className="ext" />
        <line x1={ox + 40} x2={ox + 40} y1={Y(H) - 14} y2={Y(H)} />
        <line x1={ox + 40} x2={ox + 40} y1={Y(yuk)} y2={Y(yuk) + 14} />
        <path d={`M${ox + 36} ${Y(H) - 6} L${ox + 40} ${Y(H)} L${ox + 44} ${Y(H) - 6}`} />
        <path d={`M${ox + 36} ${Y(yuk) + 6} L${ox + 40} ${Y(yuk)} L${ox + 44} ${Y(yuk) + 6}`} />
        <text x={ox + 50} y={(Y(H) + Y(yuk)) / 2 + 5} className="big">
          {f(c.stats.tavanBosluk)}
        </text>
      </g>

      {/* alt: iç genişlik */}
      <g className="kys-dimline">
        <line x1={x0} x2={x0 + Wp} y1={y0 + Hp + 22} y2={y0 + Hp + 22} />
        <line x1={x0} x2={x0} y1={y0 + Hp + 4} y2={y0 + Hp + 28} className="ext" />
        <line x1={x0 + Wp} x2={x0 + Wp} y1={y0 + Hp + 4} y2={y0 + Hp + 28} className="ext" />
        <text x={x0 + Wp / 2} y={y0 + Hp + 18} textAnchor="middle">
          {f(W)}
        </text>
      </g>
    </svg>
  );
}
